import type { Dispatch, SetStateAction } from 'react'
import { createContext, useContext } from 'react'

import {
  ContractDtoBidBasisEnum,
  type ContractDtoBidUnitEnum,
} from '@b-stock/contract-api-client'
import type { handleNewQuoteSelectionParams } from '@b-stock/fe-core/bstock-next/components'
import type { OrchestratedQuote } from '@b-stock/fe-core/bstock-next/queries'
import { BidStatus } from '@b-stock/fe-core/bstock-next/queries'

import { type AutoQuoteGenerationState } from '@/buyer-portal/components/features/listing/hooks/useAutoQuoteGeneration'
import { type CalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'

import { AuctionState } from '../const'
import type { ErrorTooltipProps } from './ErrorTooltip'

export type BidFormDetails = {
  tagStatus: BidStatus
  bidCount: number
  viewCount: number | undefined
  bidDeadline: string
  isEnded: boolean
  auctionState: AuctionState
  units: number
  msrpPrice?: number
  // T184 / FR-032 — surface the listing's currency on the bid-form
  // context so downstream renderers (BidBoxHeader, etc.) can format
  // monetary values in the originating currency rather than hardcoding
  // USD. Absence-default to USD preserves SC-007 byte-equivalence for
  // legacy / pre-cutover listings.
  currency: string
  // BP-2286 — surfaces whether the auction's bid amounts are RELATIVE
  // (e.g. % of MSRP) so downstream renderers (BidBoxHeader, etc.) can
  // format the value as a percent instead of a dollar amount.
  bidBasis: ContractDtoBidBasisEnum
  bidUnit?: ContractDtoBidUnitEnum
  bidPrecision?: number
  currentPrice: number
  userBidAmount: number
  additionalCharges: CalculatedAdditionalCharges
  isPaused: boolean
  isWithinBuyNowWindow: boolean
}

export type BidFormActions = {
  handleNewQuoteSelection: (
    params: handleNewQuoteSelectionParams
  ) => Promise<unknown>
}

export type BuyNowActions = {
  onBuyNowConfirm: () => Promise<void>
  quickBuy: () => void
}

export type BidFormContextState = {
  autoQuoteGeneration: AutoQuoteGenerationState
  bidActions: BidFormActions
  buyNowActions: BuyNowActions
  deliveryMethodChangePending: boolean
  details: BidFormDetails
  editDeliveryMethod: () => unknown
  errorTooltip: ErrorTooltipProps | null
  errorTooltipMessage: string | null
  selectedQuote?: OrchestratedQuote | null
  setErrorTooltip: Dispatch<SetStateAction<ErrorTooltipProps | null>>
  setErrorTooltipMessage: Dispatch<SetStateAction<string | null>>
  shippingQuotePending: boolean
}

const defaultState: BidFormContextState = {
  details: {
    tagStatus: BidStatus.NO_BID,
    bidCount: 0,
    viewCount: undefined,
    bidDeadline: '',
    isEnded: false,
    auctionState: AuctionState.Scheduled,
    units: 0,
    msrpPrice: 0,
    currency: 'USD',
    bidBasis: ContractDtoBidBasisEnum.Absolute,
    currentPrice: 0,
    userBidAmount: 0,
    additionalCharges: {
      total: 0,
      isWithoutShipping: false,
      bstockFee: 0,
      basis: 0,
    },
    isPaused: false,
    isWithinBuyNowWindow: false,
  },
  deliveryMethodChangePending: false,
  bidActions: {
    // Included primarily for testing, not expected to be called directly
    handleNewQuoteSelection: () => Promise.resolve(null),
  },
  buyNowActions: {
    onBuyNowConfirm: () => new Promise<void>((resolve) => resolve()),
    quickBuy: () => null,
  },
  editDeliveryMethod: () => {
    throw new Error('uninitialized editDeliveryMethod')
  },
  autoQuoteGeneration: {
    isLoading: false,
    error: null,
    status: null,
    amountCents: null,
    currency: null,
  },
  errorTooltip: null,
  errorTooltipMessage: null,
  selectedQuote: null,
  setErrorTooltip: () => {},
  setErrorTooltipMessage: () => {},
  shippingQuotePending: false,
}

export const BidFormContext = createContext<BidFormContextState>(defaultState)

export const useBidFormContext = () => useContext(BidFormContext)
